import React from 'react';
import { Layers, AlertTriangle } from 'lucide-react';
import { theme } from '../../styles/theme';
import type { PartCategoryJob } from '../../types';
import { PartCategoryStageIndicator } from './PartCategoryStageIndicator';
import { formatParseWarning } from '../../utils/errorMessages';

interface PartCategoryProcessingStatusProps {
  job: PartCategoryJob;
}

export const PartCategoryProcessingStatusComponent: React.FC<PartCategoryProcessingStatusProps> = ({
  job,
}) => {
  const details = job.processing_details;
  if (!details) return null;

  const currentStage = details.current_stage ?? 'taxonomy_fetch';
  const stageName = details.stage_name ?? 'Processing…';
  // Perf Phase 0 / Task 10 fix round: during ai_categorisation, the Stage-4
  // per-chunk liveness write updates job.progress (a model column) every
  // chunk, while processing_details.stage_progress only advances on the
  // batched checkpoint-flush cadence (every 10 chunks) — see
  // digipart_tasks.py's Stage-4 loop. Prefer job.progress here so this bar
  // still moves every chunk; other stages are unaffected and keep reading
  // stage_progress from processing_details as before.
  const stageProgress = currentStage === 'ai_categorisation'
    ? (job.progress ?? details.stage_progress ?? 0)
    : (details.stage_progress ?? 0);

  const costSoFar = details.cost?.total_cost_usd;
  const chunksCompleted = details.last_processed_chunk != null
    ? details.last_processed_chunk + 1
    : undefined;

  // Decision: PDR-061 — notes are working notes for a run in flight; a
  // `completed` run has already been through the manual validator, so the
  // taxonomy misses they list are resolved and thousands of lines of them
  // are pure noise. Deliberately narrow to `completed`: on
  // `completed_with_errors`/`failed`/`suspended` the notes are still the
  // only account of what went wrong. See
  // docs/decisions/PDR-061-completed-part-category-jobs-hide-processing-notes.md
  const notes = job.status !== 'completed' ? (details.parse_warnings ?? []) : [];

  return (
    <div className="space-y-4" data-testid="part-category-processing-status">
      {/* Stage strip */}
      <PartCategoryStageIndicator
        currentStage={currentStage}
        stageName={stageName}
        stageProgress={stageProgress}
      />

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {details.total_parts != null && (
          <StatTile
            label="Parts Fetched"
            value={details.total_parts.toLocaleString()}
            icon={<Layers className="w-4 h-4" />}
          />
        )}
        {details.uncategorised_parts != null && (
          <StatTile
            label="To Categorise"
            value={details.uncategorised_parts.toLocaleString()}
          />
        )}
        {details.chunks_total != null && (
          <StatTile
            label="Chunks"
            value={
              chunksCompleted != null
                ? `${chunksCompleted} / ${details.chunks_total}`
                : `${details.chunks_total}`
            }
          />
        )}
        {costSoFar != null && (
          <StatTile
            label="Cost so far"
            value={`$${costSoFar.toFixed(4)} USD`}
          />
        )}
      </div>

      {/* Processing notes (PDR-061 — see `notes` above). */}
      {notes.length > 0 && (
        <div className="rounded-lg border border-qc-warning/30 bg-qc-warning/10 p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-qc-warning" />
            <span className="text-sm font-semibold text-qc-warning">
              {notes.length} processing note
              {notes.length !== 1 ? 's' : ''}
            </span>
          </div>
          <ul className="space-y-1 max-h-32 overflow-y-auto">
            {notes.map((w, i) => (
              <li
                key={i}
                className="text-xs text-qc-warning"
              >
                {formatParseWarning(w)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ── Small helper tile ────────────────────────────────────────────────────────
interface StatTileProps {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

const StatTile: React.FC<StatTileProps> = ({ label, value, icon }) => (
  <div className="bg-bg-surface border border-border rounded-lg p-3 flex flex-col gap-1">
    <div className="flex items-center gap-1.5">
      {icon && <span className={theme.text.tertiary}>{icon}</span>}
      <span className={`text-xs font-medium ${theme.text.tertiary}`}>{label}</span>
    </div>
    <span className={`text-lg font-semibold ${theme.text.primary}`}>{value}</span>
  </div>
);
